import { Copy, Check } from 'lucide-react';
import { useCallback } from 'react';
import useCopyState from './useCopyState';
import CitationPillText from './CitationPillText';
import type { RespondentMap } from './useRespondentMap';

interface ArtifactThemeSummaryProps {
  title: string;
  data: {
    themes: Array<{ title: string; body: string }>;
  };
  /**
   * Respondent → conversation lookup, computed from the thread's
   * artifacts by ArtifactPanel and passed down. Used to make `[Rn]`
   * citations inside each theme body clickable — same wiring as the
   * chat bubble, so a respondent pill behaves identically wherever
   * it appears in the UI.
   *
   * Optional for back-compat with any direct callers, but always
   * supplied by ArtifactPanel in practice.
   */
  respondentMap?: RespondentMap;
}

function ArtifactThemeSummary({ title, data, respondentMap }: ArtifactThemeSummaryProps) {
  const [copied, triggerCopied] = useCopyState();

  const handleCopy = useCallback(() => {
    const text = data.themes.map((t) => `**${t.title}**\n${t.body}`).join('\n\n');
    navigator.clipboard.writeText(`${title}\n\n${text}`);
    triggerCopied();
  }, [data, title, triggerCopied]);

  if (!data?.themes?.length) return null;

  // Empty map fallback so the component remains usable when a caller
  // (e.g. a future direct embed) doesn't pass one. `[Rn]` tokens
  // still render as styled pills, just with the disabled grey state.
  const map: RespondentMap = respondentMap ?? new Map();

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-neutral-800">{title}</h3>
        <button
          type="button"
          onClick={handleCopy}
          className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded hover:bg-neutral-100 transition-colors"
          title="Copy themes"
          aria-label="Copy themes"
        >
          {copied ? (
            <Check className="h-3.5 w-3.5 text-primary" />
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
        </button>
      </div>

      {data.themes.map((theme, idx) => (
        <div
          // eslint-disable-next-line react/no-array-index-key
          key={idx}
          className="bg-white border border-neutral-200 rounded-lg p-4"
        >
          <h4 className="text-sm font-semibold text-neutral-800 mb-1.5">{theme.title}</h4>
          {/* Render the theme body with citation pills. Previously
              `<p>{theme.body}</p>` left `[R12]` tokens as raw text,
              which read as broken UX next to the same tokens being
              clickable pills in the chat bubble. */}
          <CitationPillText
            text={theme.body}
            respondentMap={map}
            variant="paragraph"
            className="text-sm text-neutral-600 leading-relaxed"
          />
        </div>
      ))}
    </div>
  );
}

export default ArtifactThemeSummary;
